import { PlexUserRelationState, TaskUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import { createStyles, makeStyles, TextField } from '@material-ui/core';
import MuiCheckIcon from '@material-ui/icons/Check';
import MuiCloseIcon from '@material-ui/icons/Close';
import MuiCreateIcon from '@material-ui/icons/Create';
import classnames from 'classnames';
import React, { SyntheticEvent, useState } from 'react';

import { useUpdateTaskMutation } from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { canEditTask } from '../../permissions';
import { TaskFieldEdit } from './common/TaskField';

type Props = {
  id: string;
  title: string;
  plexRelation: PlexUserRelationState;
  taskRelation: TaskUserRelationState;
};

type State = { type: 'initial' } | { type: 'editing'; value: string };

type PlexTaskTitleViewProps =
  | {
      mode: 'edit_inline';
      className?: string;
      value: string;
      saving: boolean;
      onChange: (value: string) => void;
      onSave: () => void;
      onCancel: () => void;
    }
  | {
      mode: 'edit';
      className?: string;
      value: string;
      saving: boolean;
      onChange: (value: string) => void;
    }
  | {
      mode: 'view';
      className?: string;
      value: string;
      onEdit: (() => void) | null;
    };

export const PlexTaskTitleView = React.forwardRef(function PlexTaskTitleView(
  props: PlexTaskTitleViewProps,
  ref: React.Ref<HTMLDivElement>
): React.ReactElement {
  const classes = useStyles();

  return (
    <div className={classnames(classes.root, props.className)} ref={ref}>
      {props.mode === 'view' && (
        <>
          <PlexTypography className={classes.title} text={props.value} size={TextSizesEnum.XXLarge} />
          {props.onEdit != null && (
            <PlexIconButton
              className={classes.editButton}
              onClick={props.onEdit}
              MuiIcon={MuiCreateIcon}
              color={ButtonColor.Purple}
              tooltip={'Edit Title'}
            />
          )}
        </>
      )}
      {(props.mode === 'edit_inline' || props.mode === 'edit') && (
        <TextField
          autoFocus={props.mode === 'edit_inline'}
          value={props.value}
          variant="outlined"
          size="small"
          disabled={props.saving}
          placeholder="Title"
          onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
            props.onChange(event.currentTarget.value);
          }}
          fullWidth
        />
      )}
      {props.mode === 'edit_inline' && (
        <>
          <PlexIconButton
            disabled={props.saving}
            className={classes.editButton}
            onClick={props.onSave}
            MuiIcon={MuiCheckIcon}
            color={ButtonColor.Purple}
            tooltip={'Save'}
          />
          <PlexIconButton
            className={classes.editButton}
            onClick={props.onCancel}
            MuiIcon={MuiCloseIcon}
            color={ButtonColor.Purple}
            tooltip={'Cancel'}
          />
        </>
      )}
    </div>
  );
});

export const PlexTaskTitle = ({ id, plexRelation, taskRelation, title }: Props): React.ReactElement => {
  const classes = useStyles();
  const [state, setState] = useState<State>({ type: 'initial' });
  const [updateTask, { loading }] = useUpdateTaskMutation();

  if (state.type === 'initial') {
    return (
      <PlexTaskTitleView
        mode="view"
        value={title}
        onEdit={
          canEditTask({ plexRelation, taskRelation })
            ? () => {
                setState({ type: 'editing', value: title });
              }
            : null
        }
      />
    );
  } else {
    return (
      <TaskFieldEdit
        saving={loading}
        onClickAway={() => {
          setState({ type: 'initial' });
        }}
      >
        <PlexTaskTitleView
          className={classes.editingRoot}
          mode="edit_inline"
          value={state.value}
          saving={loading}
          onChange={(value: string) => {
            setState({ ...state, value });
          }}
          onSave={() => {
            updateTask({ variables: { task: { id, title: state.value } } }).then(() => {
              setState({ type: 'initial' });
            });
          }}
          onCancel={() => {
            setState({ type: 'initial' });
          }}
        />
      </TaskFieldEdit>
    );
  }
};

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      display: 'inline',
      color: colorPalette.dark._900,
      fontWeight: 'bold',
    },
    editingRoot: {
      flex: 1,
      margin: `-3px 0`,
    },
    editButton: {
      display: 'inline', // needed for the edit button to be displayed at the end of the text
      marginLeft: spacings.small,
    },
    root: {
      display: 'inline-flex',
      padding: `6px 0`,
      alignItems: 'center',
    },
  })
);
